export type ResearchWorkerMode = 'research' | 'reverify' | 'manual-import';
export type CleanupStatus = 'UNVERIFIED' | 'PASS' | 'HOLD' | 'FAIL';
export type ScheduleDay = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
export type PodState = 'AZ' | 'IN' | 'KY' | 'MI' | 'OH' | 'PA' | 'WV';
export type DiscoverySource = 'osm' | 'overture';
export type PodJobKind = 'discover' | 'collect' | 'licences';
export interface PodJobParams { state?: PodState; source?: DiscoverySource }

export interface PodRow {
  name: string; states: string; slice: string; hiddenShare: string;
  vpnIp: string | null; vpnCity: string | null; worker: string | null;
  sliceSize: number | null; decided: number | null; qualified: number | null;
  hiddenDone: number | null; hiddenTotal: number | null;
}
export interface PodsStatus {
  available: boolean;
  stale: boolean;
  generatedAt?: string | null;
  ageSeconds?: number | null;
  pods: PodRow[];
  lastRun: Partial<Record<'maintain' | 'feed' | 'licences', string>>;
  recent: string[];
  job: null | { running: true; pid: number; kind: PodJobKind; params: PodJobParams; startedAt: string };
}

export interface ResearchCleanupSummary {
  total: number;
  byStatus: Record<CleanupStatus, number>;
  quarantined: number;
  archivedFails: number;
  liveCrmCount: number;
}

export interface ResearchAgentStatus {
  worker: { running: boolean; pid?: number; mode?: ResearchWorkerMode; startedAt?: string; maxMinutes?: number };
  importsPaused: boolean;
  stopRequested: boolean;
  research: null | { running?: boolean; elapsedSeconds?: number; maxMinutes?: number; lastState?: string; stopReason?: string; totals?: Record<string, number>; updatedAt?: string };
  states: Record<string, { imported: number; waiting: number; survivors: number; checked: number; qualified: number }>;
  reverify: { population: number; version: string | null; queue: Record<string, number>; decisions: Record<string, number> };
  cleanup: ResearchCleanupSummary;
  verifiedWaiting: { count: number; byState: Record<string, number>; version: string | null };
  crmLeadCount: number;
  paths: { root: string; log: string };
  /** True once the server-authority pipeline owns research state (setting pipeline.enabled). */
  pipeline: { enabled: boolean };
}

export interface CleanupRow {
  id: number;
  businessName: string;
  phone: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
  status: CleanupStatus;
  reasonCode: string | null;
  reason: string;
  websiteUrl: string | null;
  evidenceUrls: string[];
  verifierVersion: string | null;
  verifiedAt: number | null;
  archived: boolean;
}

export interface ResearchSchedule {
  id: number;
  label: string;
  mode: 'research' | 'reverify' | PodJobKind;
  params?: PodJobParams;
  startTime: string;
  maxMinutes: number;
  days: ScheduleDay[];
  enabled: boolean;
  taskName: string;
  createdAt: number;
  updatedAt: number;
}
export type ResearchScheduleInput = Omit<ResearchSchedule, 'id' | 'taskName' | 'createdAt' | 'updatedAt'>;

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

async function researchRequest<T>(path: string, options: { method?: Method; body?: unknown } = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const mutating = method !== 'GET';
  const response = await fetch(`/api${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(mutating ? { 'X-LeadForge-Request': '1' } : {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { message?: string; error?: string };
    throw new Error(payload.message ?? payload.error ?? `Request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const researchAgentApi = {
  status: () => researchRequest<ResearchAgentStatus>('/research-agent/status'),
  pods: () => researchRequest<PodsStatus>('/research-agent/pods'),
  startPodJob: (body: { kind: PodJobKind } & PodJobParams) => researchRequest<{ ok: boolean }>('/research-agent/pods/jobs', { method: 'POST', body }),
  start: (body: { mode: 'research' | 'reverify'; maxMinutes: number }) => researchRequest<{ ok: boolean; worker: ResearchAgentStatus['worker'] }>('/research-agent/start', { method: 'POST', body }),
  importPasses: () => researchRequest<{ ok: boolean; worker: ResearchAgentStatus['worker'] }>('/research-agent/import-passes', { method: 'POST', body: {} }),
  stop: () => researchRequest<{ ok: boolean; requested: boolean }>('/research-agent/stop', { method: 'POST', body: {} }),
  reassertImportsPaused: () => researchRequest<{ ok: boolean; importsPaused: boolean }>('/research-agent/imports', { method: 'POST', body: { paused: true } }),
  cleanupSummary: () => researchRequest<ResearchCleanupSummary>('/research-agent/cleanup/summary'),
  cleanup: (status: CleanupStatus, limit = 100, offset = 0) => researchRequest<{ total: number; rows: CleanupRow[] }>(`/research-agent/cleanup?status=${encodeURIComponent(status)}&limit=${limit}&offset=${offset}`),
  archiveFails: (ids?: number[]) => researchRequest<{ archived: number }>('/research-agent/cleanup/archive-fails', { method: 'POST', body: ids ? { ids } : {} }),
  schedules: () => researchRequest<ResearchSchedule[]>('/research-agent/schedules'),
  createSchedule: (body: ResearchScheduleInput) => researchRequest<ResearchSchedule>('/research-agent/schedules', { method: 'POST', body }),
  updateSchedule: ({ id, body }: { id: number; body: ResearchScheduleInput }) => researchRequest<ResearchSchedule>(`/research-agent/schedules/${id}`, { method: 'PATCH', body }),
  setScheduleEnabled: ({ id, enabled }: { id: number; enabled: boolean }) => researchRequest<ResearchSchedule>(`/research-agent/schedules/${id}/enabled`, { method: 'POST', body: { enabled } }),
  deleteSchedule: (id: number) => researchRequest<{ deleted: boolean }>(`/research-agent/schedules/${id}`, { method: 'DELETE' }),
};
